import type { JSX } from 'react';
import * as Store from '@apply/store';
import { handlePhoneValue } from '../question/question-shared';
import { NumericFormat } from 'react-number-format';
const formAction = Store.FormsStore.Types.formAction;

export type DataRowFormatType = 'none' | 'currency' | 'phone' | 'currency-short';

export const formatValue = (value, format: DataRowFormatType = 'none'): string | JSX.Element => {
    if (value === undefined || value === null) {
        return '';
    }

    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }

    if (format === 'currency') {
        return (
            <NumericFormat
                value={value}
                displayType={'text'}
                decimalScale={2}
                thousandSeparator={true}
                prefix={'$'}
            />
        );
    }

    if (format === 'currency-short') {
        const numeric = Number(value);
        if (Number.isInteger(numeric)) {
            return (
                <NumericFormat
                    value={value}
                    displayType={'text'}
                    decimalScale={0}
                    thousandSeparator={true}
                    prefix={'$'}
                />
            );
        } else {
            return (
                <NumericFormat
                    value={value}
                    displayType={'text'}
                    decimalScale={2}
                    thousandSeparator={true}
                    prefix={'$'}
                />
            );
        }
    }

    if (format === 'phone') {
        return handlePhoneValue(value as string);
    }

    if (typeof value === 'number') {
        return value.toString();
    }

    if (typeof value === 'string') {
        if (value === 'yes') {
            return 'Yes';
        } else if (value === 'no') {
            return 'No';
        } else if (value === 'unsure') {
            return 'Unsure';
        }
        return value;
    }

    if (Array.isArray(value)) {
        // will not include any values that are undefined or empty strings
        return value
            .filter((itm) => itm !== undefined && itm !== '')
            .map((itm) => formatValue(itm))
            .join('\n');
    }

    if (Store.BorrowerStore.Types.isPropertyManagementCompany(value)) {
        return formatValue([value.name, value.website, value.contactName, value.contactPhone, value.contactEmail]);
    }

    if (Store.BorrowerStore.Types.isPrincipal(value)) {
        return formatValue([value.name, value.phone, value.email, value.secondaryEmail]);
    }

    if (Store.BorrowerStore.Types.isBusinessAddress(value)) {
        if (value.county) {
            return formatValue([
                value.street1,
                value.street2,
                `${value.city}, ${value.state} ${value.zipCode}`,
                `${value.county} County`,
            ]);
        } else {
            return formatValue([value.street1, value.street2, `${value.city}, ${value.state} ${value.zipCode}`]);
        }
    }

    if (Store.PropertiesStore.Types.isAttachment(value)) {
        switch (value.handleMode) {
            case formAction.provideLater:
                return 'to be provided separately';
            case formAction.willNotProvide:
                return 'will not provide';
            case formAction.edit:
            case formAction.create:
                return 'form filled online';
            case formAction.upload:
                return 'file uploaded';
            case formAction.textData:
                return value.data;
            default:
                return '';
        }
    }

    if (Store.PropertiesStore.Types.isLandSize(value)) {
        if (!value.quantity) {
            return '';
        }
        return `${value.quantity} ${value.unit}`;
    }

    if (Store.BorrowerStore.Types.isFullName(value)) {
        return Store.BorrowerStore.Types.formatFullName(value);
    }

    console.error(
        'DataRow: unhandled value type encountered, defaulting to "toString" behavior which may provide a suboptimal experience',
        value
    );
    return `${value}`;
};
